import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

const RoleContext = createContext();

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

export const RoleProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('idToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const role = decoded['custom:role'] || 'Officer'; // Default to Officer for existing users
        setUserRole(role);
      } catch (error) {
        console.error('Error decoding token:', error);
        setUserRole(null);
      }
    }
    setIsLoading(false);
  }, []);

  const updateUserRole = (token) => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const role = decoded['custom:role'] || 'Officer';
        setUserRole(role);
      } catch (error) {
        console.error('Error decoding token:', error);
        setUserRole(null);
      }
    } else {
      setUserRole(null);
    }
  };

  // Refresh current user's role from the backend
  const refreshUserRole = async () => {
    const token = localStorage.getItem('idToken');
    if (!token) {
      setUserRole(null);
      return;
    }

    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/user/profile'
        : 'http://localhost:5000/api/user/profile';

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.role) {
        setUserRole(response.data.role);
      }
    } catch (error) {
      console.error('Error refreshing user role:', error);
      // If refresh fails, fall back to token-based role
      try {
        const decoded = jwtDecode(token);
        const role = decoded['custom:role'] || 'Officer';
        setUserRole(role);
      } catch (decodeError) {
        console.error('Error decoding token:', decodeError);
        setUserRole(null);
      }
    }
  };

  const clearUserRole = () => {
    setUserRole(null);
    setIsLoading(false);
  };

  // Force complete logout and redirect
  const forceLogout = () => {
    setUserRole(null);
    setIsLoading(false);
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.replace('/');
  };

  // Permission helper functions
  const canApproveRecords = () => {
    return userRole === 'Supervisor' || userRole === 'Admin' || userRole === 'Security Admin';
  };

  const canDeleteRecords = () => {
    return userRole === 'Supervisor' || userRole === 'Admin' || userRole === 'Security Admin';
  };

  const canViewRecords = () => {
    return userRole === 'Officer' || userRole === 'Supervisor' || userRole === 'Admin' || userRole === 'Security Admin';
  };

  const canEditRecords = () => {
    return userRole === 'Officer' || userRole === 'Supervisor' || userRole === 'Admin' || userRole === 'Security Admin';
  };

  const canCreateRecords = () => {
    return userRole === 'Officer' || userRole === 'Supervisor' || userRole === 'Admin' || userRole === 'Security Admin';
  };

  // Detention system permissions - Admin, Security Admin and Detention roles only (NOT Supervisors)
  const canAccessDetentionTabs = () => {
    return userRole === 'Detention' || userRole === 'Security Admin' || userRole === 'Admin';
  };

  const canManageUsers = () => {
    return userRole === 'Admin' || userRole === 'Security Admin';
  };

  const canAccessAuditDashboard = () => {
    return userRole === 'Security Admin';
  };

  const value = {
    userRole,
    isLoading,
    updateUserRole,
    refreshUserRole,
    clearUserRole,
    forceLogout,
    canApproveRecords,
    canDeleteRecords,
    canViewRecords,
    canEditRecords,
    canCreateRecords,
    canManageUsers,
    canAccessAuditDashboard,
    canAccessDetentionTabs,
    isSupervisor: userRole === 'Supervisor' || userRole === 'Admin' || userRole === 'Security Admin',
    isOfficer: userRole === 'Officer',
    isAdmin: userRole === 'Admin',
    isSecurityAdmin: userRole === 'Security Admin',
    isDetention: userRole === 'Detention',
    canAccessDetention: userRole === 'Detention' || userRole === 'Security Admin' || userRole === 'Admin'
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};
